import { toast } from "sonner";
import { isValidElement } from "react";

/**
 * Global toast guardrail.
 *
 * Passing a raw object/array (e.g. a FastAPI 422 `detail` array of
 * {type, loc, msg, input}) to `toast.error()` makes sonner render an object
 * as a React child → React error #31 → the root <Toaster/> throws → the whole
 * app unmounts to a BLANK PAGE (see PRD v29). Individual call sites should
 * still use `formatApiError`, but this patch makes that class of crash
 * structurally impossible no matter which call site (present or future) is at
 * fault: any non-string, non-JSX toast content is coerced to a safe string
 * before it can reach the render path.
 */
function coerceToastContent(content) {
  if (content == null) return content;
  const t = typeof content;
  if (t === "string" || t === "number" || t === "boolean") return content;
  if (t === "function") return content; // sonner render callback
  if (isValidElement(content)) return content; // intentional JSX toast
  try {
    if (Array.isArray(content)) {
      const parts = content
        .map((d) => {
          if (typeof d === "string") return d;
          if (d && typeof d === "object") return d.msg || d.message || JSON.stringify(d);
          return String(d);
        })
        .filter(Boolean)
        .slice(0, 3);
      return parts.join(" · ") || "Something went wrong";
    }
    if (t === "object") {
      if (typeof content.msg === "string") return content.msg;
      if (typeof content.message === "string") return content.message;
      if (typeof content.detail === "string") return content.detail;
      return JSON.stringify(content).slice(0, 240);
    }
  } catch (e) {
    return "Something went wrong";
  }
  return String(content);
}

let installed = false;

export function installSafeToast() {
  if (installed) return;
  installed = true;
  ["error", "success", "info", "warning", "message", "loading"].forEach((method) => {
    const original = toast[method];
    if (typeof original !== "function") return;
    toast[method] = (content, data) => original(coerceToastContent(content), data);
  });
}

export { coerceToastContent };
